import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import Header from '../Header';
import { useAuth } from '../context/AuthContext';

// Toast Notification Component
interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose, duration = 3000 }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColor = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    info: 'bg-blue-500'
  }[type];

  const icon = {
    success: '✓',
    error: '✗',
    info: 'ℹ'
  }[type];

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in">
      <div className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 min-w-80`}>
        <span className="text-xl">{icon}</span>
        <span className="flex-1">{message}</span>
        <button onClick={onClose} className="text-white hover:text-gray-200 text-lg">
          &times;
        </button>
      </div>
    </div>
  );
};

// Language Modal Component
interface Language {
  id: number;
  name: string;
  is_default: boolean;
  created_at?: string;
  updated_at?: string;
}

interface LanguageModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LanguageModal: React.FC<LanguageModalProps> = ({ isOpen, onClose }) => {
  const [languages, setLanguages] = useState<Language[]>([
    { id: 1, name: 'English', is_default: true },
    { id: 2, name: 'Afrikaans', is_default: false },
    { id: 3, name: 'Zulu', is_default: false },
  ]);
  
  const [newLanguageName, setNewLanguageName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  // const [isBrokerConsentModalOpen, setIsBrokerConsentModalOpen] = useState(false);

  // Fetch languages from API when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchLanguagesFromAPI();
    }
  }, [isOpen]);

  const fetchLanguagesFromAPI = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const response = await fetch(`${API_URL}/api/languages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch languages`);
      }

      const data = await response.json();
      if (data.success) {
        setLanguages(data.data);
        setUnsavedChanges(false);
      } else {
        throw new Error(data.message || 'Failed to fetch languages');
      }
    } catch (err: any) {
      console.error('Error fetching languages:', err);
      setError(err.message || 'Failed to load languages');
    } finally {
      setIsLoading(false);
    }
  };

  // Add new language to database
  const handleAddLanguage = async () => {
    if (!newLanguageName.trim()) {
      alert('Please enter a language name');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const response = await fetch(`${API_URL}/api/languages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newLanguageName.trim() }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to add language');
      }

      // Refresh the languages list from API
      await fetchLanguagesFromAPI();
      setNewLanguageName('');
      alert('Language added successfully!');
    } catch (err: any) {
      console.error('Error adding language:', err);
      alert(err.message || 'Failed to add language');
    } finally {
      setIsLoading(false);
    }
  };

  // Remove language from database
  const handleRemoveLanguage = async (id: number) => {
    const languageToRemove = languages.find(lang => lang.id === id);
    
    if (!languageToRemove) return;

    if (!window.confirm(`Are you sure you want to remove "${languageToRemove.name}"?`)) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const response = await fetch(`${API_URL}/api/languages/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to remove language');
      }

      // Refresh the languages list from API
      await fetchLanguagesFromAPI();
      alert('Language removed successfully!');
    } catch (err: any) {
      console.error('Error removing language:', err);
      alert(err.message || 'Failed to remove language');
    } finally {
      setIsLoading(false);
    }
  };

  // Set default language in database
  const handleSetDefault = async (id: number) => {
    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const response = await fetch(`${API_URL}/api/languages/${id}/set-default`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to set default language');
      }

      // Refresh the languages list from API
      await fetchLanguagesFromAPI();
      alert('Default language updated successfully!');
    } catch (err: any) {
      console.error('Error setting default language:', err);
      alert(err.message || 'Failed to set default language');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Save & Close - Save all changes to database
  const handleSaveAndClose = async () => {
    setIsLoading(true);
    setError('');

    try {
      // If there are any pending operations, handle them here
      // For now, just refresh from API to ensure we have latest data
      await fetchLanguagesFromAPI();
      
      // Show success message
      alert('All changes saved successfully!');
      onClose();
    } catch (err: any) {
      console.error('Error saving changes:', err);
      alert(err.message || 'Failed to save changes');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Modal Header */}
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                Manage Languages
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Add or remove languages, set the default language
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={isLoading}
            >
              &times;
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">
              {error}
            </div>
          )}

          {/* Add New Language */}
          <div className="mb-6">
            <div className="flex gap-2">
              <input
                type="text"
                value={newLanguageName}
                onChange={(e) => setNewLanguageName(e.target.value)}
                className="flex-grow px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Enter language name..."
                onKeyPress={(e) => e.key === 'Enter' && handleAddLanguage()}
                disabled={isLoading}
              />
              <button
                onClick={handleAddLanguage}
                disabled={isLoading || !newLanguageName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>

          {/* Languages List */}
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {isLoading && languages.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                Loading languages...
              </div>
            ) : languages.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                No languages found. Add your first language above.
              </div>
            ) : (
              languages.map((language) => (
                <div key={language.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="font-medium text-gray-900">
                      {language.name}
                    </span>
                    {language.is_default && (
                      <span className="ml-3 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        Default
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {!language.is_default && (
                      <>
                        <button
                          onClick={() => handleSetDefault(language.id)}
                          disabled={isLoading}
                          className="px-3 py-1 text-xs bg-gray-200 text-gray-700 hover:bg-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Set Default
                        </button>
                        <button
                          onClick={() => handleRemoveLanguage(language.id)}
                          disabled={isLoading}
                          className="px-3 py-1 text-xs bg-red-100 text-red-700 hover:bg-red-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Modal Footer - UPDATED */}
        <div className="p-6 border-t">
          <div className="text-sm text-gray-500 mb-3">
            {languages.length} language{languages.length !== 1 ? 's' : ''} configured
            {unsavedChanges && (
              <span className="ml-2 text-yellow-600">
                • Unsaved changes
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAndClose}  // Changed from onClose to handleSaveAndClose
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
            >
              {isLoading ? 'Saving...' : 'Save & Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Document Types Modal Component
interface DocumentType {
  id?: number;
  name: string;
  created_at?: string;
  updated_at?: string;
}

interface DocumentTypesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ────────────────────────────────────────────────
// Broker Consent Modal
// ────────────────────────────────────────────────

interface ConsentSentence {
  id: number;
  text: string;
  isActive: boolean;
  order: number;           // for sorting / display sequence
  createdAt?: string;
}

interface BrokerConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BrokerConsentModal: React.FC<BrokerConsentModalProps> = ({ isOpen, onClose }) => {
  const [sentences, setSentences] = useState<ConsentSentence[]>([]);
  const [newText, setNewText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');

  // Load existing sentences when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchConsentSentences();
    }
  }, [isOpen]);

  const fetchConsentSentences = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const response = await fetch(`${API_URL}/api/broker-consent-sentences`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      if (data.success) {
        setSentences(data.data || []);
      }
    } catch (err: any) {
      console.error('Failed to load consent sentences:', err);
      // Fallback: use local mock data if API not ready
      setSentences([
        { id: 1, text: "The broker confirms that all information provided is true and accurate to the best of their knowledge.", isActive: true, order: 1 },
        { id: 2, text: "The broker has explained all material terms and conditions to the client.", isActive: true, order: 2 },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSentence = async () => {
    if (!newText.trim()) return;
    
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const response = await fetch(`${API_URL}/api/broker-consent-sentences`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: newText.trim(), isActive: true, order: sentences.length + 1 }),
      });

      if (!response.ok) throw new Error('Failed to add sentence');
      
      const data = await response.json();
      setSentences([...sentences, data.data || { 
        id: Date.now(), 
        text: newText.trim(), 
        isActive: true, 
        order: sentences.length + 1 
      }]);
      
      setNewText('');
      alert('Sentence added successfully');
    } catch (err) {
      alert('Could not save sentence. It was added locally.');
      setSentences([...sentences, { 
        id: Date.now(), 
        text: newText.trim(), 
        isActive: true, 
        order: sentences.length + 1 
      }]);
      setNewText('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSentence = async (id: number) => {
    if (!editText.trim()) return;
    
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      await fetch(`${API_URL}/api/broker-consent-sentences/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: editText.trim() }),
      });

      setSentences(sentences.map(s => 
        s.id === id ? { ...s, text: editText.trim() } : s
      ));
      setEditingId(null);
      setEditText('');
    } catch (err) {
      // still update locally
      setSentences(sentences.map(s => 
        s.id === id ? { ...s, text: editText.trim() } : s
      ));
      setEditingId(null);
      setEditText('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = (id: number, current: boolean) => {
    setSentences(sentences.map(s => 
      s.id === id ? { ...s, isActive: !current } : s
    ));
    // TODO: persist to backend if needed
  };

  const handleDelete = (id: number) => {
    if (!window.confirm('Remove this consent sentence?')) return;
    setSentences(sentences.filter(s => s.id !== id));
    // TODO: DELETE request to backend
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Broker Consent Sentences</h3>
              <p className="text-sm text-gray-600 mt-1">
                Manage standard consent clauses presented to brokers
              </p>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={isLoading}
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-100 text-red-700 rounded">{error}</div>
          )}

          {/* Add new */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h4 className="font-medium mb-3">Add New Consent Sentence</h4>
            <div className="flex gap-3">
              <textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                className="flex-grow px-3 py-2 border border-gray-300 rounded resize-y min-h-[80px]"
                placeholder="Enter the full consent text / clause here..."
                disabled={isLoading}
              />
              <button
                onClick={handleAddSentence}
                disabled={isLoading || !newText.trim()}
                className="px-5 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed self-start mt-1"
              >
                {isLoading ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>

          {/* List */}
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {isLoading && sentences.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : sentences.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No consent sentences defined yet.</div>
            ) : (
              sentences
                .sort((a, b) => a.order - b.order)
                .map((sentence) => (
                  <div 
                    key={sentence.id} 
                    className={`p-4 rounded-lg border ${sentence.isActive ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-300 opacity-60'}`}
                  >
                    {editingId === sentence.id ? (
                      <div className="space-y-3">
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded resize-y min-h-[100px]"
                          autoFocus
                        />
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => { setEditingId(null); setEditText(''); }}
                            className="px-4 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleUpdateSentence(sentence.id)}
                            disabled={!editText.trim()}
                            className="px-4 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-gray-900 whitespace-pre-wrap">{sentence.text}</p>
                        <div className="mt-3 flex justify-between items-center text-sm">
                          <div className="space-x-3">
                            <button
                              onClick={() => {
                                setEditingId(sentence.id);
                                setEditText(sentence.text);
                              }}
                              className="text-blue-600 hover:underline"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleActive(sentence.id, sentence.isActive)}
                              className={sentence.isActive ? "text-amber-600 hover:underline" : "text-green-600 hover:underline"}
                            >
                              {sentence.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => handleDelete(sentence.id)}
                              className="text-red-600 hover:underline"
                            >
                              Delete
                            </button>
                          </div>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${sentence.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>
                            {sentence.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Close
          </button>
          {/* Optional: Save order / bulk update button if you later allow drag & drop sorting */}
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────
// Renewals Modal
// ────────────────────────────────────────────────

interface RenewalDefinition {
  id: number;
  question: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'select';
  options?: string[];
  isRequired: boolean;
  order: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface RenewalsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RenewalsModal: React.FC<RenewalsModalProps> = ({ isOpen, onClose }) => {
  const [questions, setQuestions] = useState<RenewalDefinition[]>([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [newType, setNewType] = useState<'text' | 'number' | 'date' | 'boolean' | 'select'>('text');
  const [newOptions, setNewOptions] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [editType, setEditType] = useState<'text' | 'number' | 'date' | 'boolean' | 'select'>('text');
  const [editOptions, setEditOptions] = useState('');
  const [editIsRequired, setEditIsRequired] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchRenewalQuestions();
    }
  }, [isOpen]);

  const fetchRenewalQuestions = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const response = await fetch(`${API_URL}/api/renewal-definitions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      if (data.success) {
        setQuestions(data.data || []);
      }
    } catch (err: any) {
      console.error('Failed to load renewal questions:', err);
      setQuestions([
        { 
          id: 1, 
          question: "Has the insured's contact information changed?", 
          type: 'boolean', 
          isRequired: true, 
          order: 1, 
          isActive: true 
        },
        { 
          id: 2, 
          question: "Has the insured's financial situation changed?", 
          type: 'boolean', 
          isRequired: false, 
          order: 2, 
          isActive: true 
        },
        { 
          id: 3, 
          question: "What is the desired renewal effective date?", 
          type: 'date', 
          isRequired: true, 
          order: 3, 
          isActive: true 
        },
        { 
          id: 4, 
          question: "Has the insured's address changed since last renewal?", 
          type: 'select', 
          options: ['Yes - Permanent Change', 'Yes - Temporary Change', 'No'], 
          isRequired: true, 
          order: 4, 
          isActive: true 
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.trim()) {
      alert('Please enter a question');
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const payload: any = {
        question: newQuestion.trim(),
        type: newType,
        isRequired: isRequired,
        order: questions.length + 1,
        isActive: true,
      };

      if (newType === 'select' && newOptions.trim()) {
        payload.options = newOptions.split(',').map(opt => opt.trim());
      }

      const response = await fetch(`${API_URL}/api/renewal-definitions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to add question');

      const data = await response.json();
      setQuestions([...questions, data.data || { 
        id: Date.now(), 
        question: newQuestion.trim(), 
        type: newType, 
        options: newType === 'select' ? newOptions.split(',').map(opt => opt.trim()) : undefined,
        isRequired: isRequired, 
        order: questions.length + 1, 
        isActive: true 
      }]);

      setNewQuestion('');
      setNewType('text');
      setNewOptions('');
      setIsRequired(false);
      alert('Question added successfully');
    } catch (err) {
      setQuestions([...questions, { 
        id: Date.now(), 
        question: newQuestion.trim(), 
        type: newType, 
        options: newType === 'select' ? newOptions.split(',').map(opt => opt.trim()) : undefined,
        isRequired: isRequired, 
        order: questions.length + 1, 
        isActive: true 
      }]);
      setNewQuestion('');
      setNewType('text');
      setNewOptions('');
      setIsRequired(false);
      alert('Question added locally');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateQuestion = async (id: number) => {
    if (!editQuestion.trim()) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const payload: any = {
        question: editQuestion.trim(),
        type: editType,
        isRequired: editIsRequired,
      };

      if (editType === 'select' && editOptions.trim()) {
        payload.options = editOptions.split(',').map(opt => opt.trim());
      } else if (editType === 'select') {
        payload.options = [];
      }

      await fetch(`${API_URL}/api/renewal-definitions/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      setQuestions(questions.map(q => 
        q.id === id ? { 
          ...q, 
          question: editQuestion.trim(), 
          type: editType,
          options: editType === 'select' ? editOptions.split(',').map(opt => opt.trim()) : undefined,
          isRequired: editIsRequired 
        } : q
      ));
      setEditingId(null);
      setEditQuestion('');
      setEditType('text');
      setEditOptions('');
      setEditIsRequired(false);
      alert('Question updated successfully');
    } catch (err) {
      setQuestions(questions.map(q => 
        q.id === id ? { 
          ...q, 
          question: editQuestion.trim(), 
          type: editType,
          options: editType === 'select' ? editOptions.split(',').map(opt => opt.trim()) : undefined,
          isRequired: editIsRequired 
        } : q
      ));
      setEditingId(null);
      setEditQuestion('');
      setEditType('text');
      setEditOptions('');
      setEditIsRequired(false);
      alert('Question updated locally');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = (id: number, current: boolean) => {
    setQuestions(questions.map(q => 
      q.id === id ? { ...q, isActive: !current } : q
    ));
  };

  const handleDelete = (id: number) => {
    if (!window.confirm('Remove this renewal question?')) return;
    setQuestions(questions.filter(q => q.id !== id));
  };

  const handleEditStart = (question: RenewalDefinition) => {
    setEditingId(question.id);
    setEditQuestion(question.question);
    setEditType(question.type);
    setEditOptions(question.options ? question.options.join(', ') : '');
    setEditIsRequired(question.isRequired);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Renewal Definition Questions</h3>
              <p className="text-sm text-gray-600 mt-1">
                Manage questions asked during the renewal process
              </p>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={isLoading}
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-100 text-red-700 rounded">{error}</div>
          )}

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <h4 className="font-medium mb-3">Add New Renewal Question</h4>
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-grow">
                  <input
                    type="text"
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="Enter renewal question..."
                    disabled={isLoading}
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={newType}
                    onChange={(e) => setNewType(e.target.value as any)}
                    className="px-3 py-2 border border-gray-300 rounded bg-white"
                    disabled={isLoading}
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="boolean">Yes/No</option>
                    <option value="select">Select</option>
                  </select>
                </div>
              </div>

              {newType === 'select' && (
                <div>
                  <input
                    type="text"
                    value={newOptions}
                    onChange={(e) => setNewOptions(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="Options (comma separated, e.g., Option 1, Option 2, Option 3)"
                    disabled={isLoading}
                  />
                </div>
              )}

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isRequired}
                    onChange={(e) => setIsRequired(e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600"
                    disabled={isLoading}
                  />
                  Required field
                </label>
                <button
                  onClick={handleAddQuestion}
                  disabled={isLoading || !newQuestion.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Adding...' : 'Add Question'}
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-3 max-h-80 overflow-y-auto">
            {isLoading && questions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Loading questions...</div>
            ) : questions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No renewal questions defined yet.</div>
            ) : (
              questions
                .sort((a, b) => a.order - b.order)
                .map((question) => (
                  <div 
                    key={question.id} 
                    className={`p-4 rounded-lg border ${question.isActive ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-300 opacity-60'}`}
                  >
                    {editingId === question.id ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={editQuestion}
                          onChange={(e) => setEditQuestion(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded"
                          autoFocus
                        />
                        <div className="flex flex-wrap gap-3">
                          <select
                            value={editType}
                            onChange={(e) => setEditType(e.target.value as any)}
                            className="px-3 py-2 border border-gray-300 rounded bg-white"
                          >
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                            <option value="boolean">Yes/No</option>
                            <option value="select">Select</option>
                          </select>
                          {editType === 'select' && (
                            <input
                              type="text"
                              value={editOptions}
                              onChange={(e) => setEditOptions(e.target.value)}
                              className="flex-grow px-3 py-2 border border-gray-300 rounded"
                              placeholder="Options (comma separated)"
                            />
                          )}
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={editIsRequired}
                              onChange={(e) => setEditIsRequired(e.target.checked)}
                              className="form-checkbox h-4 w-4 text-blue-600"
                            />
                            Required
                          </label>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => { setEditingId(null); setEditQuestion(''); }}
                            className="px-4 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleUpdateQuestion(question.id)}
                            disabled={!editQuestion.trim()}
                            className="px-4 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="text-gray-900 font-medium">{question.question}</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700`}>
                                {question.type}
                              </span>
                              {question.isRequired && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                  Required
                                </span>
                              )}
                              {question.options && question.options.length > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                  {question.options.length} options
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => handleEditStart(question)}
                              className="px-3 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleActive(question.id, question.isActive)}
                              className={`px-3 py-1 text-xs rounded ${
                                question.isActive 
                                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                            >
                              {question.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => handleDelete(question.id)}
                              className="px-3 py-1 text-xs bg-red-100 text-red-700 hover:bg-red-200 rounded"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          Order: {question.order}
                        </div>
                      </>
                    )}
                  </div>
                ))
            )}
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────
// Return Debits Modal
// ────────────────────────────────────────────────

interface ReturnDebitDefinition {
  id: number;
  question: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'select';
  options?: string[];
  isRequired: boolean;
  order: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface ReturnDebitsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ReturnDebitsModal: React.FC<ReturnDebitsModalProps> = ({ isOpen, onClose }) => {
  const [questions, setQuestions] = useState<ReturnDebitDefinition[]>([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [newType, setNewType] = useState<'text' | 'number' | 'date' | 'boolean' | 'select'>('text');
  const [newOptions, setNewOptions] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [editType, setEditType] = useState<'text' | 'number' | 'date' | 'boolean' | 'select'>('text');
  const [editOptions, setEditOptions] = useState('');
  const [editIsRequired, setEditIsRequired] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchReturnDebitQuestions();
    }
  }, [isOpen]);

  const fetchReturnDebitQuestions = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const response = await fetch(`${API_URL}/api/return-debit-definitions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      if (data.success) {
        setQuestions(data.data || []);
      }
    } catch (err: any) {
      console.error('Failed to load return debit questions:', err);
      setQuestions([
        { 
          id: 1, 
          question: "Has the client confirmed the return debit amount?", 
          type: 'boolean', 
          isRequired: true, 
          order: 1, 
          isActive: true 
        },
        { 
          id: 2, 
          question: "Has the policy been cancelled for the return debit?", 
          type: 'boolean', 
          isRequired: false, 
          order: 2, 
          isActive: true 
        },
        { 
          id: 3, 
          question: "What is the reason for the return debit?", 
          type: 'select', 
          options: ['Overpayment', 'Cancellation', 'Correction', 'Other'], 
          isRequired: true, 
          order: 3, 
          isActive: true 
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.trim()) {
      alert('Please enter a question');
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const payload: any = {
        question: newQuestion.trim(),
        type: newType,
        isRequired: isRequired,
        order: questions.length + 1,
        isActive: true,
      };

      if (newType === 'select' && newOptions.trim()) {
        payload.options = newOptions.split(',').map(opt => opt.trim());
      }

      const response = await fetch(`${API_URL}/api/return-debit-definitions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to add question');

      const data = await response.json();
      setQuestions([...questions, data.data || { 
        id: Date.now(), 
        question: newQuestion.trim(), 
        type: newType, 
        options: newType === 'select' ? newOptions.split(',').map(opt => opt.trim()) : undefined,
        isRequired: isRequired, 
        order: questions.length + 1, 
        isActive: true 
      }]);

      setNewQuestion('');
      setNewType('text');
      setNewOptions('');
      setIsRequired(false);
      alert('Question added successfully');
    } catch (err) {
      setQuestions([...questions, { 
        id: Date.now(), 
        question: newQuestion.trim(), 
        type: newType, 
        options: newType === 'select' ? newOptions.split(',').map(opt => opt.trim()) : undefined,
        isRequired: isRequired, 
        order: questions.length + 1, 
        isActive: true 
      }]);
      setNewQuestion('');
      setNewType('text');
      setNewOptions('');
      setIsRequired(false);
      alert('Question added locally');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateQuestion = async (id: number) => {
    if (!editQuestion.trim()) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const payload: any = {
        question: editQuestion.trim(),
        type: editType,
        isRequired: editIsRequired,
      };

      if (editType === 'select' && editOptions.trim()) {
        payload.options = editOptions.split(',').map(opt => opt.trim());
      } else if (editType === 'select') {
        payload.options = [];
      }

      await fetch(`${API_URL}/api/return-debit-definitions/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      setQuestions(questions.map(q => 
        q.id === id ? { 
          ...q, 
          question: editQuestion.trim(), 
          type: editType,
          options: editType === 'select' ? editOptions.split(',').map(opt => opt.trim()) : undefined,
          isRequired: editIsRequired 
        } : q
      ));
      setEditingId(null);
      setEditQuestion('');
      setEditType('text');
      setEditOptions('');
      setEditIsRequired(false);
      alert('Question updated successfully');
    } catch (err) {
      setQuestions(questions.map(q => 
        q.id === id ? { 
          ...q, 
          question: editQuestion.trim(), 
          type: editType,
          options: editType === 'select' ? editOptions.split(',').map(opt => opt.trim()) : undefined,
          isRequired: editIsRequired 
        } : q
      ));
      setEditingId(null);
      setEditQuestion('');
      setEditType('text');
      setEditOptions('');
      setEditIsRequired(false);
      alert('Question updated locally');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = (id: number, current: boolean) => {
    setQuestions(questions.map(q => 
      q.id === id ? { ...q, isActive: !current } : q
    ));
  };

  const handleDelete = (id: number) => {
    if (!window.confirm('Remove this return debit question?')) return;
    setQuestions(questions.filter(q => q.id !== id));
  };

  const handleEditStart = (question: ReturnDebitDefinition) => {
    setEditingId(question.id);
    setEditQuestion(question.question);
    setEditType(question.type);
    setEditOptions(question.options ? question.options.join(', ') : '');
    setEditIsRequired(question.isRequired);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Return Debit Definition Questions</h3>
              <p className="text-sm text-gray-600 mt-1">
                Manage questions asked during the return debit process
              </p>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={isLoading}
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-100 text-red-700 rounded">{error}</div>
          )}

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <h4 className="font-medium mb-3">Add New Return Debit Question</h4>
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-grow">
                  <input
                    type="text"
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="Enter return debit question..."
                    disabled={isLoading}
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={newType}
                    onChange={(e) => setNewType(e.target.value as any)}
                    className="px-3 py-2 border border-gray-300 rounded bg-white"
                    disabled={isLoading}
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="boolean">Yes/No</option>
                    <option value="select">Select</option>
                  </select>
                </div>
              </div>

              {newType === 'select' && (
                <div>
                  <input
                    type="text"
                    value={newOptions}
                    onChange={(e) => setNewOptions(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="Options (comma separated, e.g., Option 1, Option 2, Option 3)"
                    disabled={isLoading}
                  />
                </div>
              )}

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isRequired}
                    onChange={(e) => setIsRequired(e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600"
                    disabled={isLoading}
                  />
                  Required field
                </label>
                <button
                  onClick={handleAddQuestion}
                  disabled={isLoading || !newQuestion.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Adding...' : 'Add Question'}
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-3 max-h-80 overflow-y-auto">
            {isLoading && questions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Loading questions...</div>
            ) : questions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No return debit questions defined yet.</div>
            ) : (
              questions
                .sort((a, b) => a.order - b.order)
                .map((question) => (
                  <div 
                    key={question.id} 
                    className={`p-4 rounded-lg border ${question.isActive ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-300 opacity-60'}`}
                  >
                    {editingId === question.id ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={editQuestion}
                          onChange={(e) => setEditQuestion(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded"
                          autoFocus
                        />
                        <div className="flex flex-wrap gap-3">
                          <select
                            value={editType}
                            onChange={(e) => setEditType(e.target.value as any)}
                            className="px-3 py-2 border border-gray-300 rounded bg-white"
                          >
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                            <option value="boolean">Yes/No</option>
                            <option value="select">Select</option>
                          </select>
                          {editType === 'select' && (
                            <input
                              type="text"
                              value={editOptions}
                              onChange={(e) => setEditOptions(e.target.value)}
                              className="flex-grow px-3 py-2 border border-gray-300 rounded"
                              placeholder="Options (comma separated)"
                            />
                          )}
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={editIsRequired}
                              onChange={(e) => setEditIsRequired(e.target.checked)}
                              className="form-checkbox h-4 w-4 text-blue-600"
                            />
                            Required
                          </label>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => { setEditingId(null); setEditQuestion(''); }}
                            className="px-4 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleUpdateQuestion(question.id)}
                            disabled={!editQuestion.trim()}
                            className="px-4 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="text-gray-900 font-medium">{question.question}</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700`}>
                                {question.type}
                              </span>
                              {question.isRequired && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                  Required
                                </span>
                              )}
                              {question.options && question.options.length > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                  {question.options.length} options
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => handleEditStart(question)}
                              className="px-3 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleActive(question.id, question.isActive)}
                              className={`px-3 py-1 text-xs rounded ${
                                question.isActive 
                                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                            >
                              {question.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => handleDelete(question.id)}
                              className="px-3 py-1 text-xs bg-red-100 text-red-700 hover:bg-red-200 rounded"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          Order: {question.order}
                        </div>
                      </>
                    )}
                  </div>
                ))
            )}
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────
// Claims Modal
// ────────────────────────────────────────────────

interface ClaimDefinition {
  id: number;
  question: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'select';
  options?: string[];
  isRequired: boolean;
  order: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface ClaimsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ClaimsModal: React.FC<ClaimsModalProps> = ({ isOpen, onClose }) => {
  const [questions, setQuestions] = useState<ClaimDefinition[]>([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [newType, setNewType] = useState<'text' | 'number' | 'date' | 'boolean' | 'select'>('text');
  const [newOptions, setNewOptions] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [editType, setEditType] = useState<'text' | 'number' | 'date' | 'boolean' | 'select'>('text');
  const [editOptions, setEditOptions] = useState('');
  const [editIsRequired, setEditIsRequired] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchClaimQuestions();
    }
  }, [isOpen]);

  const fetchClaimQuestions = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const response = await fetch(`${API_URL}/api/claim-definitions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      if (data.success) {
        setQuestions(data.data || []);
      }
    } catch (err: any) {
      console.error('Failed to load claim questions:', err);
      setQuestions([
        { 
          id: 1, 
          question: "Has the incident been reported to the police?", 
          type: 'boolean', 
          isRequired: true, 
          order: 1, 
          isActive: true 
        },
        { 
          id: 2, 
          question: "Has the claim been submitted to the insurer?", 
          type: 'boolean', 
          isRequired: true, 
          order: 2, 
          isActive: true 
        },
        { 
          id: 3, 
          question: "Has the client provided all required documentation?", 
          type: 'boolean', 
          isRequired: true, 
          order: 3, 
          isActive: true 
        },
        { 
          id: 4, 
          question: "Has the claim been assessed by the adjuster?", 
          type: 'boolean', 
          isRequired: false, 
          order: 4, 
          isActive: true 
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.trim()) {
      alert('Please enter a question');
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const payload: any = {
        question: newQuestion.trim(),
        type: newType,
        isRequired: isRequired,
        order: questions.length + 1,
        isActive: true,
      };

      if (newType === 'select' && newOptions.trim()) {
        payload.options = newOptions.split(',').map(opt => opt.trim());
      }

      const response = await fetch(`${API_URL}/api/claim-definitions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to add question');

      const data = await response.json();
      setQuestions([...questions, data.data || { 
        id: Date.now(), 
        question: newQuestion.trim(), 
        type: newType, 
        options: newType === 'select' ? newOptions.split(',').map(opt => opt.trim()) : undefined,
        isRequired: isRequired, 
        order: questions.length + 1, 
        isActive: true 
      }]);

      setNewQuestion('');
      setNewType('text');
      setNewOptions('');
      setIsRequired(false);
      alert('Question added successfully');
    } catch (err) {
      setQuestions([...questions, { 
        id: Date.now(), 
        question: newQuestion.trim(), 
        type: newType, 
        options: newType === 'select' ? newOptions.split(',').map(opt => opt.trim()) : undefined,
        isRequired: isRequired, 
        order: questions.length + 1, 
        isActive: true 
      }]);
      setNewQuestion('');
      setNewType('text');
      setNewOptions('');
      setIsRequired(false);
      alert('Question added locally');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateQuestion = async (id: number) => {
    if (!editQuestion.trim()) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const payload: any = {
        question: editQuestion.trim(),
        type: editType,
        isRequired: editIsRequired,
      };

      if (editType === 'select' && editOptions.trim()) {
        payload.options = editOptions.split(',').map(opt => opt.trim());
      } else if (editType === 'select') {
        payload.options = [];
      }

      await fetch(`${API_URL}/api/claim-definitions/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      setQuestions(questions.map(q => 
        q.id === id ? { 
          ...q, 
          question: editQuestion.trim(), 
          type: editType,
          options: editType === 'select' ? editOptions.split(',').map(opt => opt.trim()) : undefined,
          isRequired: editIsRequired 
        } : q
      ));
      setEditingId(null);
      setEditQuestion('');
      setEditType('text');
      setEditOptions('');
      setEditIsRequired(false);
      alert('Question updated successfully');
    } catch (err) {
      setQuestions(questions.map(q => 
        q.id === id ? { 
          ...q, 
          question: editQuestion.trim(), 
          type: editType,
          options: editType === 'select' ? editOptions.split(',').map(opt => opt.trim()) : undefined,
          isRequired: editIsRequired 
        } : q
      ));
      setEditingId(null);
      setEditQuestion('');
      setEditType('text');
      setEditOptions('');
      setEditIsRequired(false);
      alert('Question updated locally');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = (id: number, current: boolean) => {
    setQuestions(questions.map(q => 
      q.id === id ? { ...q, isActive: !current } : q
    ));
  };

  const handleDelete = (id: number) => {
    if (!window.confirm('Remove this claim question?')) return;
    setQuestions(questions.filter(q => q.id !== id));
  };

  const handleEditStart = (question: ClaimDefinition) => {
    setEditingId(question.id);
    setEditQuestion(question.question);
    setEditType(question.type);
    setEditOptions(question.options ? question.options.join(', ') : '');
    setEditIsRequired(question.isRequired);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Claim Definition Questions</h3>
              <p className="text-sm text-gray-600 mt-1">
                Manage questions asked during the claim process
              </p>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 text-2xl"
              disabled={isLoading}
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-100 text-red-700 rounded">{error}</div>
          )}

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <h4 className="font-medium mb-3">Add New Claim Question</h4>
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-grow">
                  <input
                    type="text"
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="Enter claim question..."
                    disabled={isLoading}
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={newType}
                    onChange={(e) => setNewType(e.target.value as any)}
                    className="px-3 py-2 border border-gray-300 rounded bg-white"
                    disabled={isLoading}
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="boolean">Yes/No</option>
                    <option value="select">Select</option>
                  </select>
                </div>
              </div>

              {newType === 'select' && (
                <div>
                  <input
                    type="text"
                    value={newOptions}
                    onChange={(e) => setNewOptions(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    placeholder="Options (comma separated, e.g., Option 1, Option 2, Option 3)"
                    disabled={isLoading}
                  />
                </div>
              )}

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isRequired}
                    onChange={(e) => setIsRequired(e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600"
                    disabled={isLoading}
                  />
                  Required field
                </label>
                <button
                  onClick={handleAddQuestion}
                  disabled={isLoading || !newQuestion.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Adding...' : 'Add Question'}
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-3 max-h-80 overflow-y-auto">
            {isLoading && questions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Loading questions...</div>
            ) : questions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No claim questions defined yet.</div>
            ) : (
              questions
                .sort((a, b) => a.order - b.order)
                .map((question) => (
                  <div 
                    key={question.id} 
                    className={`p-4 rounded-lg border ${question.isActive ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-300 opacity-60'}`}
                  >
                    {editingId === question.id ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={editQuestion}
                          onChange={(e) => setEditQuestion(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded"
                          autoFocus
                        />
                        <div className="flex flex-wrap gap-3">
                          <select
                            value={editType}
                            onChange={(e) => setEditType(e.target.value as any)}
                            className="px-3 py-2 border border-gray-300 rounded bg-white"
                          >
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                            <option value="boolean">Yes/No</option>
                            <option value="select">Select</option>
                          </select>
                          {editType === 'select' && (
                            <input
                              type="text"
                              value={editOptions}
                              onChange={(e) => setEditOptions(e.target.value)}
                              className="flex-grow px-3 py-2 border border-gray-300 rounded"
                              placeholder="Options (comma separated)"
                            />
                          )}
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={editIsRequired}
                              onChange={(e) => setEditIsRequired(e.target.checked)}
                              className="form-checkbox h-4 w-4 text-blue-600"
                            />
                            Required
                          </label>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => { setEditingId(null); setEditQuestion(''); }}
                            className="px-4 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleUpdateQuestion(question.id)}
                            disabled={!editQuestion.trim()}
                            className="px-4 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="text-gray-900 font-medium">{question.question}</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700`}>
                                {question.type}
                              </span>
                              {question.isRequired && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                  Required
                                </span>
                              )}
                              {question.options && question.options.length > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                  {question.options.length} options
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => handleEditStart(question)}
                              className="px-3 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleActive(question.id, question.isActive)}
                              className={`px-3 py-1 text-xs rounded ${
                                question.isActive 
                                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                            >
                              {question.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => handleDelete(question.id)}
                              className="px-3 py-1 text-xs bg-red-100 text-red-700 hover:bg-red-200 rounded"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          Order: {question.order}
                        </div>
                      </>
                    )}
                  </div>
                ))
            )}
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}; 

const DocumentTypesModal: React.FC<DocumentTypesModalProps> = ({ isOpen, onClose }) => {
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([
    { id: 1, name: 'Company Registration Documents' },
    { id: 2, name: 'Identification Document' },
    { id: 3, name: 'Other' }
  ]);
  
  const [newDocumentTypeName, setNewDocumentTypeName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState<number | null>(null);
  const [editName, setEditName] = useState('');

  // Toast notifications state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<{ id: number; name: string } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type });
  };

  const closeToast = () => {
    setToast(null);
  };

  // Fetch document types from API when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchDocumentTypesFromAPI();
    }
  }, [isOpen]);

  const fetchDocumentTypesFromAPI = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      
      const response = await fetch(`${API_URL}/api/document-types`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // If endpoint doesn't exist, use default types
        console.log('Using default document types');
        return;
      }

      const data = await response.json();
      if (data.success) {
        setDocumentTypes(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch document types');
      }
    } catch (err: any) {
      console.log('Using default document types');
    } finally {
      setIsLoading(false);
    }
  };

  // Add new document type
  const handleAddDocumentType = async () => {
    if (!newDocumentTypeName.trim()) {
      showToast('Please enter a document type name', 'error');
      return;
    }

    // Check for duplicates
    if (documentTypes.some(doc => doc.name.toLowerCase() === newDocumentTypeName.toLowerCase().trim())) {
      showToast('A document type with this name already exists', 'error');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const newDocType: DocumentType = {
        name: newDocumentTypeName.trim()
      };

      // Try to save to API
      const response = await fetch(`${API_URL}/api/document-types`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newDocType),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Add to local state with ID from API
          setDocumentTypes(prev => [...prev, { ...newDocType, id: data.data.id }]);
          showToast('Document type added successfully!');
        } else {
          // Add to local state without ID
          const newId = Math.max(...documentTypes.map(d => d.id || 0), 0) + 1;
          setDocumentTypes(prev => [...prev, { ...newDocType, id: newId }]);
          showToast('Document type added successfully!');
        }
      } else {
        // Add to local state
        const newId = Math.max(...documentTypes.map(d => d.id || 0), 0) + 1;
        setDocumentTypes(prev => [...prev, { ...newDocType, id: newId }]);
        showToast('Document type added successfully!');
      }

      setNewDocumentTypeName('');
    } catch (err: any) {
      console.error('Error adding document type:', err);
      // Add to local state anyway
      const newId = Math.max(...documentTypes.map(d => d.id || 0), 0) + 1;
      setDocumentTypes(prev => [...prev, { 
        name: newDocumentTypeName.trim(),
        id: newId 
      }]);
      setNewDocumentTypeName('');
      showToast('Document type added successfully!');
    } finally {
      setIsLoading(false);
    }
  };

  // Start editing a document type
  const handleStartEdit = (docType: DocumentType) => {
    setIsEditing(docType.id || null);
    setEditName(docType.name);
  };

  // Save edited document type
  const handleSaveEdit = async (id: number) => {
    if (!editName.trim()) {
      showToast('Name cannot be empty', 'error');
      return;
    }

    // Check for duplicates (excluding current item)
    if (documentTypes.some(doc => doc.id !== id && doc.name.toLowerCase() === editName.toLowerCase().trim())) {
      showToast('Another document type with this name already exists', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const updatedDocType = {
        name: editName.trim()
      };

      // Try to update via API
      const response = await fetch(`${API_URL}/api/document-types/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedDocType),
      });

      // Update local state
      setDocumentTypes(prev => prev.map(doc => 
        doc.id === id ? { ...updatedDocType, id } : doc
      ));

      setIsEditing(null);
      setEditName('');
      showToast('Document type updated successfully!');
    } catch (err: any) {
      console.error('Error updating document type:', err);
      // Update local state anyway
      setDocumentTypes(prev => prev.map(doc => 
        doc.id === id ? { 
          ...doc, 
          name: editName.trim()
        } : doc
      ));
      setIsEditing(null);
      setEditName('');
      showToast('Document type updated successfully!');
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(null);
    setEditName('');
  };

  // Handle remove button click (shows confirmation modal)
  const handleRemoveClick = (id: number) => {
    const docTypeToRemove = documentTypes.find(doc => doc.id === id);
    if (docTypeToRemove) {
      setDocumentToDelete({ id, name: docTypeToRemove.name });
      setShowConfirmModal(true);
    }
  };

  // Handle confirmed deletion
  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;
    
    const { id } = documentToDelete;
    setShowConfirmModal(false);
    
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const response = await fetch(`${API_URL}/api/document-types/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setDocumentTypes(prev => prev.filter(doc => doc.id !== id));
          showToast('Document type removed successfully!');
        } else {
          showToast(data.message || 'Failed to remove document type', 'error');
        }
      } else {
        // Still remove from local state for immediate feedback
        setDocumentTypes(prev => prev.filter(doc => doc.id !== id));
        showToast('Document type removed from local state', 'info');
      }
    } catch (err: any) {
      console.error('Error removing document type:', err);
      setDocumentTypes(prev => prev.filter(doc => doc.id !== id));
      showToast('Document type removed (offline mode)', 'info');
    } finally {
      setIsLoading(false);
      setDocumentToDelete(null);
    }
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setDocumentToDelete(null);
  };

  // Save & Close
  const handleSaveAndClose = async () => {
    setIsLoading(true);
    try {
      // If you want to save all changes to API, you can implement bulk update here
      showToast('Document types saved successfully!');
      
      // Wait a moment for the toast to be seen, then close the modal
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('Error saving changes:', err);
      showToast(err.message || 'Failed to save changes', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
        />
      )}

      {/* Confirmation Modal for Delete */}
      {showConfirmModal && documentToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-70 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                Confirm Delete
              </h3>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to remove <span className="font-semibold">"{documentToDelete.name}"</span>?
              </p>
              <p className="text-sm text-gray-500 mb-6">
                This action cannot be undone.
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleCancelDelete}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400"
                >
                  {isLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-40 flex items-center justify-center">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
          {/* Modal Header */}
          <div className="p-6 border-b">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Manage Document Types
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Add, edit, or remove document types used in the system
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl"
                disabled={isLoading}
              >
                &times;
              </button>
            </div>
          </div>

          {/* Modal Content */}
          <div className="p-6">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">
                {error}
              </div>
            )}

            {/* Add New Document Type */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <h4 className="font-medium text-gray-900 mb-3">Add New Document Type</h4>
              <div className="flex gap-3">
                <div className="flex-grow">
                  <input
                    type="text"
                    value={newDocumentTypeName}
                    onChange={(e) => setNewDocumentTypeName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="e.g., Company Registration Documents"
                    disabled={isLoading}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddDocumentType()}
                  />
                </div>
                <div>
                  <button
                    onClick={handleAddDocumentType}
                    disabled={isLoading || !newDocumentTypeName.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
            </div>

            {/* Document Types List */}
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {isLoading && documentTypes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Loading document types...
                </div>
              ) : documentTypes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No document types found. Add your first document type above.
                </div>
              ) : (
                documentTypes.map((docType) => (
                  <div key={docType.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    {isEditing === docType.id ? (
                      // Edit Mode
                      <div className="space-y-3">
                        <div className="flex gap-3">
                          <div className="flex-grow">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              placeholder="Document Type Name"
                              onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit(docType.id!)}
                            />
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleSaveEdit(docType.id!)}
                            disabled={isLoading || !editName.trim()}
                            className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={isLoading}
                            className="px-3 py-1 bg-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-400"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="mb-1">
                              <span className="font-medium text-gray-900">
                                {docType.name}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600">
                              Created: {docType.created_at ? new Date(docType.created_at).toLocaleDateString() : 'N/A'}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleStartEdit(docType)}
                              disabled={isLoading}
                              className="px-3 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleRemoveClick(docType.id!)}
                              disabled={isLoading}
                              className="px-3 py-1 text-xs bg-red-100 text-red-700 hover:bg-red-200 rounded"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="p-6 border-t">
            <div className="text-sm text-gray-500 mb-3">
              {documentTypes.length} document type{documentTypes.length !== 1 ? 's' : ''} configured
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAndClose}
                disabled={isLoading}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
              >
                {isLoading ? 'Saving...' : 'Save & Close'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Main Process Definitions Component
const ProcessDefinitions = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [isDocumentTypesModalOpen, setIsDocumentTypesModalOpen] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  // const [isBrokerConsentModalOpen, setIsBrokerConsentModalOpen] = useState(false);
  const [isRenewalsModalOpen, setIsRenewalsModalOpen] = useState(false);
  const [isReturnDebitsModalOpen, setIsReturnDebitsModalOpen] = useState(false);
  const [isClaimsModalOpen, setIsClaimsModalOpen] = useState(false);
  
  const processButtons = [
    { 
      name: 'Language', 
      icon: '🌐', 
      onClick: () => setIsLanguageModalOpen(true) 
    },
    { 
      name: 'Documents',
      icon: '📁',
      onClick: () => setIsDocumentTypesModalOpen(true) 
    },

    { 
      name: 'Application Form', 
      icon: '📋',  
      onClick: () => navigate('/application-insurers') 
    },

    // Deferred — backend not built yet. See ticket.
// { 
//   name: 'Broker Consent', 
//   icon: '✅', 
//   onClick: () => setIsBrokerConsentModalOpen(true) 
// },

    { 
      name: 'Instructions', 
      icon: '📋', 
      onClick: () => navigate('/process-definitions/instructions') 
    },

    { 
      name: 'Claims', 
      icon: '⚖️', 
      onClick: () => setIsClaimsModalOpen(true) 
    },
    { 
      name: 'Policies', 
      icon: '📄', 
      onClick: () => navigate('/process-definitions/policies') 
    },
    { 
      name: 'Renewals', 
      icon: '🔄', 
      onClick: () => setIsRenewalsModalOpen(true) 
    },
    { 
      name: 'Return Debits', 
      icon: '💳', 
      onClick: () => setIsReturnDebitsModalOpen(true) 
    },
    { 
      name: 'Quotations', 
      icon: '💬', 
      onClick: () => navigate('/process-definitions/quotation-declarations')
  }
  ];

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  return (
    <>
      {/* Sidebar */}
      <Sidebar 
        isVisible={isSidebarVisible} 
        toggleSidebar={toggleSidebar} 
      />

      {/* Main Content Area */}
      <div className={`transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        {/* Header */}
        <Header 
          title="Process Definitions"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />

        {/* Page Content */}
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
          <div className="max-w-4xl mx-auto">
            {/* Optional: Additional page header if needed */}
            <div className="mb-8">
              <p className="text-gray-600">
                Configure system processes and workflows
              </p>
            </div>

            {/* Vertical Buttons - Clean layout */}
            <div className="space-y-3">
              {processButtons.map((button, index) => (
                <button
                  key={index}
                  onClick={button.onClick}
                  className="w-full flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 shadow-sm"
                >
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{button.icon}</span>
                    <span className="text-gray-900 font-medium">
                      {button.name}
                    </span>
                  </div>
                  <svg 
                    className="w-5 h-5 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Language Modal */}
      <LanguageModal 
        isOpen={isLanguageModalOpen} 
        onClose={() => setIsLanguageModalOpen(false)} 
      />

      {/* Document Types Modal */}
      <DocumentTypesModal 
        isOpen={isDocumentTypesModalOpen} 
        onClose={() => setIsDocumentTypesModalOpen(false)} 
      />

      {/* <BrokerConsentModal isOpen={isBrokerConsentModalOpen} onClose={() => setIsBrokerConsentModalOpen(false)} /> */}
      {/* Renewals Modal */}
      <RenewalsModal 
        isOpen={isRenewalsModalOpen} 
        onClose={() => setIsRenewalsModalOpen(false)} 
      />

      {/* Return Debits Modal */}
      <ReturnDebitsModal 
        isOpen={isReturnDebitsModalOpen} 
        onClose={() => setIsReturnDebitsModalOpen(false)} 
      />

      {/* Claims Modal */}
      <ClaimsModal 
        isOpen={isClaimsModalOpen} 
        onClose={() => setIsClaimsModalOpen(false)} 
      />

    </>
  );
};

export default ProcessDefinitions;